/**
 * OTRIX Play — shared front-end constants + i18n label helpers.
 *
 * GraphQL enums serialize by NAME (e.g. "CHEST"), so the values below are the uppercase
 * enum names the API returns and expects in variables. Visible labels come from the
 * `play` i18n namespace via the helpers (pass the component's `t`). `region` maps a
 * muscle group to a clickable zone on the body map SVG.
 */

export const MUSCLE_GROUPS = [
  { value: 'CHEST', region: 'chest' },
  { value: 'BACK', region: 'back' },
  { value: 'TRAPEZIUS', region: 'trapezius' },
  { value: 'SHOULDERS', region: 'shoulders' },
  { value: 'BICEPS', region: 'biceps' },
  { value: 'TRICEPS', region: 'triceps' },
  { value: 'FOREARMS', region: 'forearms' },
  { value: 'ABS', region: 'abs' },
  { value: 'OBLIQUES', region: 'obliques' },
  { value: 'LOWER_BACK', region: 'lower_back' },
  { value: 'GLUTES', region: 'glutes' },
  { value: 'QUADRICEPS', region: 'quadriceps' },
  { value: 'ADDUCTORS', region: 'adductors' },
  { value: 'HAMSTRINGS', region: 'hamstrings' },
  { value: 'CALVES', region: 'calves' },
  { value: 'CARDIO', region: 'cardio' },
  { value: 'FULL_BODY', region: 'full_body' }
];

// Map our MuscleGroup enum -> react-muscle-highlighter slugs (anatomical body map).
export const MUSCLE_SLUGS = {
  CHEST: ['chest'],
  BACK: ['upper-back'],
  TRAPEZIUS: ['trapezius'],
  SHOULDERS: ['deltoids'],
  BICEPS: ['biceps'],
  TRICEPS: ['triceps'],
  FOREARMS: ['forearm'],
  ABS: ['abs'],
  OBLIQUES: ['obliques'],
  LOWER_BACK: ['lower-back'],
  GLUTES: ['gluteal'],
  ADDUCTORS: ['adductors'],
  QUADRICEPS: ['quadriceps'],
  HAMSTRINGS: ['hamstring'],
  CALVES: ['calves']
};

// Reverse lookup: library slug -> our enum.
export const slugToMuscle = Object.entries(MUSCLE_SLUGS).reduce((acc, [en, slugs]) => {
  slugs.forEach((s) => {
    acc[s] = en;
  });
  return acc;
}, {});

/**
 * Los 17 grupos del enum MuscleGroup agregados en las 7 regiones del radar de "Mi Rango".
 *
 * Van separadas por miembro (brazos aparte de hombros, piernas aparte de core) a propósito:
 * el radar existe para que un desbalance real se VEA. Si se agregaran en bloques grandes
 * (p. ej. "tren superior"), una pierna que nunca se entrena quedaría compensada por el resto
 * del bloque y el hueco desaparecería del gráfico, que es justo lo contrario de su función.
 */
export const MUSCLE_REGIONS = {
  CHEST: ['CHEST'],
  BACK: ['BACK', 'LOWER_BACK', 'TRAPEZIUS'],
  SHOULDERS: ['SHOULDERS'],
  ARMS: ['BICEPS', 'TRICEPS', 'FOREARMS'],
  LEGS: ['QUADRICEPS', 'HAMSTRINGS', 'GLUTES', 'CALVES', 'ADDUCTORS'],
  CORE: ['ABS', 'OBLIQUES'],
  CARDIO: ['CARDIO']
};

// Orden de los vértices del radar (sentido horario desde arriba).
export const REGION_VALUES = ['SHOULDERS', 'CHEST', 'ARMS', 'BACK', 'LEGS', 'CORE', 'CARDIO'];

// FULL_BODY no es una región: reparte su peso entre las que sí entrena un ejercicio integral.
const FULL_BODY_REGIONS = REGION_VALUES.filter((r) => r !== 'CARDIO');

// Reverse lookup: grupo muscular -> región del radar.
export const regionOfMuscle = Object.entries(MUSCLE_REGIONS).reduce((acc, [region, muscles]) => {
  muscles.forEach((m) => {
    acc[m] = region;
  });
  return acc;
}, {});

/**
 * Reparte pesos por grupo muscular en las 7 regiones y los normaliza a % del total.
 *
 * `entries` = [{ muscle, weight }]. Se normaliza porque las dos series del radar vienen en
 * unidades distintas (resultados registrados vs. series planificadas) y en crudo no son
 * comparables; el % del total sí lo es.
 *
 * SIEMPRE devuelve las 7 regiones, rellenando con 0 las que no tienen datos: una región que
 * falta no dibuja vértice, y un músculo sin entrenar es precisamente lo que hay que ver.
 */
export const volumeByRegion = (entries) => {
  const totals = REGION_VALUES.reduce((acc, r) => ({ ...acc, [r]: 0 }), {});

  (entries || []).forEach(({ muscle, weight }) => {
    const w = Number(weight) || 0;
    if (!muscle || w <= 0) return;
    if (muscle === 'FULL_BODY') {
      const share = w / FULL_BODY_REGIONS.length;
      FULL_BODY_REGIONS.forEach((r) => {
        totals[r] += share;
      });
      return;
    }
    const region = regionOfMuscle[muscle];
    if (region) totals[region] += w;
  });

  const sum = Object.values(totals).reduce((s, v) => s + v, 0);
  if (!sum) return REGION_VALUES.map((region) => ({ region, percent: 0 }));

  return REGION_VALUES.map((region) => ({ region, percent: Math.round((totals[region] / sum) * 1000) / 10 }));
};

// Muscles visible per body-map view (used for the list presentation).
export const FRONT_MUSCLES = ['SHOULDERS', 'TRAPEZIUS', 'CHEST', 'BICEPS', 'FOREARMS', 'ABS', 'OBLIQUES', 'ADDUCTORS', 'QUADRICEPS', 'CALVES'];
export const BACK_MUSCLES = ['TRAPEZIUS', 'SHOULDERS', 'BACK', 'TRICEPS', 'FOREARMS', 'LOWER_BACK', 'GLUTES', 'ADDUCTORS', 'HAMSTRINGS', 'CALVES'];

export const LOCATIONS = [{ value: 'HOME' }, { value: 'GYM' }, { value: 'BOTH' }];

// Taxonomía de ejecución (columna `exercise_type`). Alimenta el chip de las tarjetas.
export const EXERCISE_TYPES = [{ value: 'COMPOUND' }, { value: 'ISOLATION' }, { value: 'CALISTHENICS' }];

// Intensidad de un día de la rutina (enum `RoutineIntensity`). La deriva el backend desde las
// reps del día y el usuario puede fijarla desde el menú de la tarjeta del día.
export const INTENSITY_VALUES = ['STRENGTH', 'HYPERTROPHY', 'ENDURANCE'];

// ISO-8601: 1 = Monday ... 7 = Sunday.
export const DAY_VALUES = [1, 2, 3, 4, 5, 6, 7];

export const METRIC_VALUES = ['REPS', 'TIME_SECONDS', 'DISTANCE_KM', 'LEVEL'];

// Niveles de enfoque del mapa de distribución: 1 = bajo, 2 = medio, 3 = alto.
export const FOCUS_LEVELS = { LOW: 1, MEDIUM: 2, HIGH: 3 };
const FOCUS_KEYS = { 1: 'low', 2: 'medium', 3: 'high' };

// Label helpers — each takes the component's `t` (from useTranslation).
// Las claves de `muscles.*` van en MAYÚSCULAS (igual que el enum). Se normaliza y se deja un
// `defaultValue` para que un valor inesperado muestre algo legible en vez de la clave cruda.
export const muscleLabel = (t, value) =>
  value ? t(`muscles.${String(value).toUpperCase()}`, { ns: 'play', defaultValue: String(value) }) : '';
export const muscleRegionLabel = (t, value) => (value ? t(`regions.${value}`, { ns: 'play' }) : '');
export const locationLabel = (t, value) => (value ? t(`locations.${value}`, { ns: 'play' }) : '');
export const exerciseTypeLabel = (t, value) => (value ? t(`exerciseTypes.${value}`, { ns: 'play' }) : '');
export const intensityLabel = (t, value) => (value ? t(`intensity.${value}`, { ns: 'play' }) : '');
export const focusLevelLabel = (t, level) => (FOCUS_KEYS[level] ? t(`focus.${FOCUS_KEYS[level]}`, { ns: 'play' }) : '');
export const dayLabel = (t, value) => t(`days.${value}.label`, { ns: 'play' });
export const dayShort = (t, value) => t(`days.${value}.short`, { ns: 'play' });
export const metricLabel = (t, value) => (value ? t(`metrics.${value}`, { ns: 'play' }) : '');

// ISO day for "today" — JS getDay(): 0=Sun..6=Sat → ISO 1=Mon..7=Sun.
export const todayIso = () => ((new Date().getDay() + 6) % 7) + 1;

/**
 * Músculo dominante de un conjunto de items de rutina: el `muscle_group` más frecuente.
 * Es lo que titula el día ("Martes · Pecho") y lo que alimenta el foco del motor de ejecución.
 */
export const primaryMuscleOf = (items) => {
  const counts = {};
  (items || []).forEach((i) => {
    const m = i.exercise?.muscle_group;
    if (m) counts[m] = (counts[m] || 0) + 1;
  });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return top ? top[0] : null;
};

/**
 * Intensidad de un día concreto a partir del array `days` de la rutina.
 * Devuelve null en días de descanso (el backend solo guarda los días con ejercicios).
 */
export const intensityOf = (days, day) => (days || []).find((d) => d.day_of_week === day)?.intensity || null;

/**
 * Items de un día, ya ordenados por el backend (day_of_week, position).
 */
export const itemsOfDay = (items, day) => (items || []).filter((i) => i.day_of_week === day);

/**
 * Ubicación agregada de un día: si todos los ejercicios comparten sitio se muestra ese,
 * si se mezclan (o alguno ya es BOTH) se resuelve como "Ambos".
 */
export const dayLocationOf = (items) => {
  const set = new Set((items || []).map((i) => i.exercise?.location).filter(Boolean));
  if (set.size === 0) return null;
  if (set.size === 1) return [...set][0];
  return 'BOTH';
};

/**
 * Distribución muscular de la semana → nivel 1..3 por músculo, relativo al más trabajado.
 * OJO: es la distribución de la RUTINA (cliente). `myMuscleDistribution` del servidor es
 * otra cosa: la de los resultados ya registrados (ranking / Mi Rango).
 */
export const muscleDistributionOf = (items) => {
  const counts = {};
  (items || []).forEach((i) => {
    const m = i.exercise?.muscle_group;
    if (m) counts[m] = (counts[m] || 0) + 1;
  });
  const max = Math.max(1, ...Object.values(counts));
  const level = (c) => {
    if (c >= max * 0.66) return FOCUS_LEVELS.HIGH;
    if (c >= max * 0.34) return FOCUS_LEVELS.MEDIUM;
    return FOCUS_LEVELS.LOW;
  };
  return Object.entries(counts).reduce((acc, [m, c]) => {
    acc[m] = level(c);
    return acc;
  }, {});
};

/* ------------------------- Fotogramas de ejercicio -------------------------- */

/**
 * Proporción de los fotogramas de un ejercicio Play: **9:16 vertical**.
 *
 * Fuente ÚNICA: la usan el recorte del admin (`ExerciseFrameUploader`), la card y el panel de
 * detalle del catálogo. Antes estaba escrita a mano como '3 / 4' en 6 sitios; ese ratio servía
 * cuando el catálogo era torso (hombros, pecho), pero en un ejercicio de piernas recortaba justo
 * lo que hay que ver. 9:16 muestra el cuerpo completo y es la proporción de las fotos de origen.
 *
 * `StopMotionImage` pinta con `objectFit: contain`, así que los fotogramas viejos en 3:4 no se
 * deforman: quedan con barras a los lados hasta que se vuelvan a recortar.
 */
export const FRAME_ASPECT = 9 / 16;
export const FRAME_ASPECT_CSS = '9 / 16';

/**
 * Alto de la tira de miniaturas (fotograma 1 / fotograma 2 / vista previa).
 *
 * Se fija el ALTO y el ancho lo deriva `aspectRatio`, no al revés: si la miniatura ocupa el
 * tercio de la card, a 9:16 el alto sale de 1,78 × el ancho de la columna y la card se dispara
 * (eran ~194px de foto en una card de ~340). Con el alto fijo los tres recuadros miden
 * exactamente lo mismo en cualquier ancho de card.
 */
export const FRAME_THUMB_HEIGHT = { xs: 116, sm: 132 };

/** Ancho de los tres recuadros del cargador del admin (el alto lo pone `aspectRatio`: ~196px). */
export const FRAME_SLOT_WIDTH = 110;
